import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Modal,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUser,
  FaCreditCard,
  FaUniversity,
  FaRupeeSign,
  FaShieldAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";

import FinoDmtService from "../../../services/FinoDmtService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SideBar from "../../../components/Sidebar";

const AddBeneficiary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { customerMobile, data } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [token, setToken] = useState(null);

  const [formData, setFormData] = useState({
    bene_name: "",
    account_no: "",
    ifsc_code: "",
    bank_id: "",
  });
  const [errors, setErrors] = useState({});

  // Fetch bank list on component mount
  useEffect(() => {
    fetchBankList();
  }, []);

  const fetchBankList = async () => {
    setLoadingBanks(true);
    try {
      // Assuming there is an API endpoint to fetch banks
      // Adjust the method name as per your service
      const response = await FinoDmtService.bankList();
      if (
        response.success &&
        response.data &&
        response.data.status === "SUCCESS"
      ) {
        setBankList(response.data.data || []);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "Failed to load bank list",
        });
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoadingBanks(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;
    if (name === "account_no") {
      formattedValue = value.replace(/\D/g, "");
    } else if (name === "ifsc_code") {
      formattedValue = value.toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.bene_name.trim()) {
      newErrors.bene_name = "Beneficiary name is required";
    } else if (formData.bene_name.length < 3) {
      newErrors.bene_name = "Name must be at least 3 characters";
    }

    if (!formData.account_no.trim()) {
      newErrors.account_no = "Account number is required";
    } else if (
      formData.account_no.length < 9 ||
      formData.account_no.length > 18
    ) {
      newErrors.account_no = "Account number must be 9-18 digits";
    }

    if (!formData.ifsc_code.trim()) {
      newErrors.ifsc_code = "IFSC code is required";
    } else if (formData.ifsc_code.length !== 11) {
      newErrors.ifsc_code = "IFSC must be 11 characters";
    }

    if (!formData.bank_id) {
      newErrors.bank_id = "Please select a bank";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission - call add beneficiary OTP API
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix all errors before submitting",
        timer: 2000,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        bene_name: formData.bene_name,
        bank_id: formData.bank_id,
        account_no: formData.account_no,
        ifsc_code: formData.ifsc_code,
      };

      const result = await FinoDmtService.addBeneSendOtp(
        customerMobile,
        formData.bene_name,
        formData.bank_id,
        formData.account_no,
        formData.ifsc_code,
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: result.message || "Request failed!",
        });
        return;
      }

      const res = result.data;
      if (res.status === "SUCCESS") {
        // Store reference if any (e.g., beneId or remitterId) for OTP verification
        setToken(res.data?.token);
        setShowOtpModal(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res.message || "Something went wrong",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and complete beneficiary addition
  const handleVerifyOtp = async () => {
    if (!otp) {
      setOtpError("Please enter a valid OTP");
      return;
    }
    setOtpError("");
    setLoading(true);

    try {
      // Assuming there is a verify API endpoint
      // Adjust the method name as per your service
      const result = await FinoDmtService.addBeneVerifyOtp(
        customerMobile,
        formData.bene_name,
        formData.bank_id,
        formData.account_no,
        formData.ifsc_code,
        otp,
        token,
        // Add any other required fields
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: result.message || "Invalid OTP or request failed",
        });
        return;
      }

      const res = result.data;
      if (res.status === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: res.message,
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          setShowOtpModal(false);
          navigate("/fino-money-transfer/sender-home", {
            state: { customerMobile, data },
          });
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: res.message || "OTP verification failed",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/fino-money-transfer/sender-home", {
      state: { customerMobile, data },
    });
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />
        <div className="col-md-9 mb-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title pb-2">Add Beneficiary</h5>

              <Form onSubmit={handleSubmit}>
                {/* Beneficiary Name */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaUser className="me-2" /> Beneficiary Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="bene_name"
                    value={formData.bene_name}
                    onChange={handleChange}
                    placeholder="Enter beneficiary full name"
                    isInvalid={!!errors.bene_name}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.bene_name}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Account Number */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaCreditCard className="me-2" /> Account Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="account_no"
                    value={formData.account_no}
                    onChange={handleChange}
                    placeholder="Enter account number"
                    maxLength={18}
                    isInvalid={!!errors.account_no}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.account_no}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* IFSC Code */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    <FaUniversity className="me-2" /> IFSC Code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={handleChange}
                    placeholder="Enter 11-character IFSC code"
                    maxLength={11}
                    isInvalid={!!errors.ifsc_code}
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ifsc_code}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Bank Select Dropdown */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    <FaUniversity className="me-2" /> Select Bank
                  </Form.Label>
                  <Form.Select
                    name="bank_id"
                    value={formData.bank_id}
                    onChange={handleChange}
                    isInvalid={!!errors.bank_id}
                    disabled={loading || loadingBanks}
                  >
                    <option value="">-- Select Bank --</option>
                    {bankList.map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.bank_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.bank_id}
                  </Form.Control.Feedback>
                  {loadingBanks && (
                    <div className="text-muted mt-1">
                      <Spinner animation="border" size="sm" className="me-1" />
                      Loading banks...
                    </div>
                  )}
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <Button
                    variant="outline-secondary"
                    onClick={handleGoBack}
                    disabled={loading}
                  >
                    Back to Dashboard
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading || loadingBanks}
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Add Beneficiary
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>

      {/* OTP Verification Modal */}
      <Modal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Verify OTP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            OTP has been sent to sender registered mobile number. Please enter
            the OTP to complete beneficiary addition.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Enter OTP</Form.Label>
            <Form.Control
              type="text"
              maxLength={6}
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, ""));
                if (otpError) setOtpError("");
              }}
              placeholder="OTP"
              isInvalid={!!otpError}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {otpError}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowOtpModal(false);
              setOtp("");
              setOtpError("");
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleVerifyOtp}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Verifying...
              </>
            ) : (
              "Verify OTP"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddBeneficiary;
